import { useRef, useState } from 'react';

import { deletePoint, movePoint, type PointEdit } from './curve-math.js';

/**
 * Local draft of a point list while a gesture runs (drag, add + drag), so
 * the document gets one change (one undo step) when it ends. Structural
 * edits are collected for parallel arrays (sweep scales, loft sections).
 */
export function usePointDraft<T>(points: readonly T[], commit: (next: T[], edits: PointEdit[]) => void) {
  const [draft, setDraft] = useState<{ points: T[]; edits: PointEdit[] } | null>(null);
  const ref = useRef<{ points: T[]; edits: PointEdit[] } | null>(null);
  const update = (fn: (d: { points: T[]; edits: PointEdit[] }) => { points: T[]; edits: PointEdit[] }) => {
    const next = fn(ref.current ?? { points: points.slice(), edits: [] });
    ref.current = next;
    setDraft(next);
  };
  return {
    points: (draft?.points ?? points) as readonly T[],
    move(index: number, p: T) {
      update((d) => ({ ...d, points: movePoint(d.points, index, p) }));
    },
    /** Append (or with `at`, insert) a point; returns its index. */
    add(p: T, at?: number, from?: number | null): number {
      let index = 0;
      update((d) => {
        index = at === undefined ? d.points.length : Math.min(at, d.points.length);
        const pts = d.points.slice();
        pts.splice(index, 0, p);
        const edit: PointEdit = at === undefined ? { kind: 'add', index, from: from ?? null } : { kind: 'insert', index };
        return { points: pts, edits: [...d.edits, edit] };
      });
      return index;
    },
    /** Delete immediately (its own undo step). */
    remove(index: number) {
      ref.current = null;
      setDraft(null);
      commit(deletePoint(points, index), [{ kind: 'delete', index }]);
    },
    finish() {
      const d = ref.current;
      ref.current = null;
      setDraft(null);
      if (d) commit(d.points, d.edits);
    },
  };
}
